import React, { useEffect } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
  Stack,
  Card,
  CardContent,
  Chip,
  useTheme,
  alpha,
  Divider,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  AdminPanelSettingsOutlined,
  ManageAccountsOutlined,
  PersonOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MuiTelInput } from "mui-tel-input";
import { URLS, PHONE_CONFIG } from "../../constants";
import { useToast } from "../../components/ToastProvider";
import profileIcon from "../../assets/settings/settings_profile.svg";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { createPhoneNumberHandler } from "../../utils/phoneNumberInputHandler";
import { normalizePhoneNumberForParsing } from "../../utils/formatPhoneNumber";
import api from "../../utils/api";
import logger from "../../utils/logger";
import { useDashboardTheme } from "../Dashboard/context/DashboardThemeContext";

interface AuthUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  telephone?: string;
  role: "ADMIN" | "EMPLOYEE" | "SUPERADMIN" | "BASIC" | "DEV";
  language: string;
}

function getAuthUser(): AuthUser | undefined {
  const raw = localStorage.getItem("authUser");
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    logger.error("invalid authUser in localStorage");
    return undefined;
  }
}

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  telephone?: string;
  language: string;
};

export default function ProfilePage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = useDashboardTheme();
  const textSecondary = isDark ? "rgba(255,255,255,0.5)" : "text.secondary";
  const cardBg = isDark
    ? `linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)`
    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.tertiary.main, 0.02)} 100%)`;
  const inputSx: SxProps<Theme> = isDark
    ? {
        "& .MuiOutlinedInput-root": {
          color: "#fff",
          backgroundColor: "rgba(255,255,255,0.05)",
          "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
          "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
          "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
        },
        "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
        "& .MuiInputBase-input": {
          color: "#fff",
          "&::placeholder": { color: "rgba(255,255,255,0.35)", opacity: 1 },
        },
        "& .MuiSelect-icon": { color: "rgba(255,255,255,0.5)" },
        "& .MuiFormHelperText-root": { color: "rgba(255,255,255,0.45)" },
        "& .MuiFormHelperText-root.Mui-error": {
          color: theme.palette.error.light,
        },
      }
    : {};
  const [authUser] = React.useState(() => getAuthUser());
  const { showToast } = useToast();

  const detectedCountry = React.useMemo(() => {
    const normalized = normalizePhoneNumberForParsing(authUser?.telephone);
    const parsed = parsePhoneNumberFromString(normalized);
    return (parsed?.country as any) || PHONE_CONFIG.DEFAULT_COUNTRY;
  }, [authUser]);

  const defaultValues = React.useMemo(() => {
    if (!authUser) return undefined;

    const normalized = normalizePhoneNumberForParsing(authUser.telephone);
    const parsed = parsePhoneNumberFromString(normalized);
    const e164 = parsed?.number || authUser.telephone || "";

    return {
      firstName: authUser.firstName,
      lastName: authUser.lastName,
      email: authUser.email,
      telephone: e164,
      language: authUser.language,
    };
  }, [authUser]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<FormValues>({
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (!authUser) return;

    // Clean telephone: remove non-digit characters
    const cleanedTelephone = data.telephone?.replace(/\D/g, "") || "";

    const payload = {
      id: authUser.id,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: cleanedTelephone,
      gender: (authUser as any).gender || null,
      role: authUser.role,
      language: data.language,
    };

    try {
      const res = await api.put(`${URLS.CLIENT_URL}/api/user`, payload);
      if (res.status !== 200) throw new Error("Failed to update user");

      const updatedUser = {
        ...authUser,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        telephone: cleanedTelephone,
        language: data.language,
      };
      localStorage.setItem("authUser", JSON.stringify(updatedUser));

      reset(data);
      showToast(t("toast.userUpdated"), "success");
    } catch (err) {
      logger.error("onSubmit", err);
      showToast(t("toast.errorUpdate"), "error");
    }
  };

  if (!authUser) {
    return (
      <Box
        sx={{
          maxWidth: 600,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          py: 4,
        }}
      >
        <CircularProgress />
        <Typography sx={{ color: textSecondary }}>
          {t("profilePage.loadingUser")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        maxWidth: 600,
        mx: "auto",
        display: "grid",
        gap: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 80,
            height: 80,
            mb: 2,
          }}
        >
          <img
            src={profileIcon}
            alt="Profile"
            style={{
              width: "80px",
              height: "80px",
              objectFit: "contain",
            }}
          />
        </Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}
        >
          {t("profilePage.title")}
        </Typography>
        <Typography variant="body2" sx={{ color: textSecondary }}>
          {t("profilePage.description")}
        </Typography>
      </Box>

      <Divider
        sx={{ borderColor: isDark ? "rgba(255,255,255,0.1)" : undefined }}
      />

      {/* Form Fields */}
      <Stack spacing={3}>
        {/* Nome e Sobrenome */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: theme.palette.primary.main,
              }}
            >
              {t("profilePage.firstName")}
            </Typography>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder={t("placeholder.enterName")}
                  fullWidth
                  variant="outlined"
                  sx={inputSx}
                />
              )}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: theme.palette.primary.main,
              }}
            >
              {t("profilePage.lastName")}
            </Typography>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder={t("placeholder.enterLastName")}
                  fullWidth
                  variant="outlined"
                  sx={inputSx}
                />
              )}
            />
          </Box>
        </Box>

        {/* Email */}
        <Box>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: theme.palette.primary.main,
            }}
          >
            {t("profilePage.email")}
          </Typography>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder={t("placeholder.enterEmail")}
                type="email"
                fullWidth
                variant="outlined"
                sx={inputSx}
              />
            )}
          />
        </Box>

        {/* Telephone and Language */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: theme.palette.primary.main,
              }}
            >
              {t("profilePage.telephone")}
            </Typography>
            <Controller
              name="telephone"
              control={control}
              render={({ field }) => {
                return (
                  <MuiTelInput
                    value={field.value || ""}
                    onChange={createPhoneNumberHandler(
                      field.onChange,
                      field.value,
                    )}
                    defaultCountry={detectedCountry}
                    placeholder={t("placeholder.enterPhone")}
                    forceCallingCode
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        backgroundColor: "transparent",
                      },
                      ...inputSx,
                    }}
                  />
                );
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: theme.palette.primary.main,
              }}
            >
              {t("profilePage.language")}
            </Typography>
            <Controller
              name="language"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  placeholder={t("placeholder.selectLanguage")}
                  fullWidth
                  variant="outlined"
                  sx={inputSx}
                >
                  <MenuItem value="Brazilian Portuguese">
                    {t("addTeammate.language.pt")}
                  </MenuItem>
                  <MenuItem value="English">
                    {t("addTeammate.language.en")}
                  </MenuItem>
                  <MenuItem value="Spanish">
                    {t("addTeammate.language.es")}
                  </MenuItem>
                </TextField>
              )}
            />
          </Box>
        </Box>

        {/* Account Information */}
        {(() => {
          const isSuperAdmin = authUser.role === "SUPERADMIN";
          const isAdmin = authUser.role === "ADMIN";

          const roleLabel = isSuperAdmin
            ? "Gestor da Plataforma"
            : isAdmin
              ? t("profilePage.admin")
              : t("profilePage.employee");

          const roleDescription = isSuperAdmin
            ? "Acesso total à plataforma — gestão de empresas, usuários e configurações globais."
            : isAdmin
              ? "Administrador da empresa — gestão de equipe e configurações."
              : "Acesso padrão à plataforma.";

          const RoleIcon = isSuperAdmin
            ? AdminPanelSettingsOutlined
            : isAdmin
              ? ManageAccountsOutlined
              : PersonOutlined;

          const accentColor = isSuperAdmin
            ? "#7956F7"
            : isAdmin
              ? theme.palette.primary.main
              : isDark
                ? "rgba(255,255,255,0.5)"
                : theme.palette.text.secondary;

          const accentBg = isSuperAdmin
            ? isDark
              ? "rgba(121,86,247,0.12)"
              : "rgba(121,86,247,0.07)"
            : isDark
              ? "rgba(255,255,255,0.04)"
              : alpha(theme.palette.primary.main, 0.05);

          const accentBorder = isSuperAdmin
            ? isDark
              ? "rgba(121,86,247,0.35)"
              : "rgba(121,86,247,0.2)"
            : isDark
              ? "rgba(255,255,255,0.08)"
              : alpha(theme.palette.primary.main, 0.1);

          const superAdminPrivileges = [
            "Gerenciar todas as empresas",
            "Gerenciar usuários globais",
            "Configurações da plataforma",
            "Analytics e relatórios globais",
            "Controle de créditos (Tacits)",
            "Ativar / desativar Jesss por empresa",
          ];

          const adminPrivileges = [
            "Gerenciar equipe da empresa",
            "Configurações da empresa",
            "Redefinir senhas de usuários",
          ];

          const privileges = isSuperAdmin
            ? superAdminPrivileges
            : isAdmin
              ? adminPrivileges
              : [];

          return (
            <Card
              sx={{
                background: accentBg,
                border: `1px solid ${accentBorder}`,
                borderRadius: "12px",
                boxShadow: isSuperAdmin
                  ? isDark
                    ? "0 0 20px rgba(121,86,247,0.12)"
                    : "0 2px 12px rgba(121,86,247,0.08)"
                  : "none",
              }}
            >
              <CardContent sx={{ py: 2.5, px: 3 }}>
                {/* Header row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: privileges.length > 0 ? 2 : 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      background: isSuperAdmin
                        ? "linear-gradient(135deg, #7956F7 0%, #4C3699 100%)"
                        : isDark
                          ? "rgba(255,255,255,0.08)"
                          : alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: isSuperAdmin
                        ? "0 3px 10px rgba(121,86,247,0.35)"
                        : "none",
                    }}
                  >
                    <RoleIcon
                      sx={{
                        fontSize: 20,
                        color: isSuperAdmin ? "#fff" : accentColor,
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: isDark ? "#fff" : "#111",
                          lineHeight: 1.2,
                        }}
                      >
                        {roleLabel}
                      </Typography>
                      {isSuperAdmin && (
                        <Chip
                          label="Acesso Total"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.62rem",
                            fontWeight: 700,
                            background:
                              "linear-gradient(135deg, #7956F7 0%, #4C3699 100%)",
                            color: "#fff",
                            border: "none",
                            "& .MuiChip-label": { px: 0.9 },
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: textSecondary,
                        lineHeight: 1.4,
                        display: "block",
                        mt: 0.25,
                      }}
                    >
                      {roleDescription}
                    </Typography>
                  </Box>
                </Box>

                {/* Privileges list */}
                {privileges.length > 0 && (
                  <>
                    <Divider
                      sx={{
                        borderColor: isSuperAdmin
                          ? isDark
                            ? "rgba(121,86,247,0.2)"
                            : "rgba(121,86,247,0.12)"
                          : isDark
                            ? "rgba(255,255,255,0.07)"
                            : "rgba(0,0,0,0.06)",
                        mb: 1.5,
                      }}
                    />
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 0.75,
                      }}
                    >
                      {privileges.map((p) => (
                        <Box
                          key={p}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <CheckCircleOutline
                            sx={{
                              fontSize: 14,
                              color: isSuperAdmin ? "#a78bfa" : accentColor,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: textSecondary,
                              fontSize: "0.75rem",
                              lineHeight: 1.3,
                            }}
                          >
                            {p}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })()}
      </Stack>

      <Divider
        sx={{ borderColor: isDark ? "rgba(255,255,255,0.1)" : undefined }}
      />

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={!isDirty || isSubmitting}
            fullWidth
            size="large"
            sx={{
              fontWeight: 600,
              py: 1.5,
              borderRadius: 2,
            }}
          >
            {isSubmitting ? t("Saving...") : t("common.save")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
